class FaqChatWidget extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.panelWrapper = this.querySelector('[data-faq-chat-panel-wrapper]');
    this.triggerButton = this.querySelector('[data-faq-chat-open]');
    this.body = this.querySelector('[data-faq-chat-body]');
    this.menuPanel = this.querySelector('[data-panel="menu"]');
    this.conversationPanel = this.querySelector('[data-panel="conversation"]');
    this.conversationEl = this.querySelector('[data-faq-chat-conversation]');
    this.searchInput = this.querySelector('[data-faq-chat-search]');
    this.topicsEl = this.querySelector('[data-faq-chat-topics]');
    this.emptyStateEl = this.querySelector('[data-faq-chat-empty]');
    this.teaserEl = this.querySelector('[data-faq-chat-teaser]');

    this.addEventListener('click', this.handleClick);
    if (this.searchInput) {
      this.searchInput.addEventListener('input', () => this.filterTopics(this.searchInput.value));
    }

    document.addEventListener('keydown', this.handleKeydown);
    document.addEventListener('click', this.handleOutsideClick);

    this.initTeaser();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('click', this.handleOutsideClick);
  }

  handleKeydown = (event) => {
    if (event.key === 'Escape' && this.isOpen()) {
      this.closeDialog();
    }
  };

  handleOutsideClick = (event) => {
    if (!this.isOpen()) return;
    if (this.contains(event.target)) return;
    this.closeDialog();
  };

  initTeaser() {
    if (!this.teaserEl || this.dataset.teaserEnabled !== 'true') return;

    // Um lojista pode ter várias páginas abertas na mesma navegação — mostramos
    // o aviso só uma vez por sessão do navegador (sessionStorage), pra não ficar
    // repetindo em toda troca de página.
    if (sessionStorage.getItem('faqChatTeaserDismissed') === 'true') return;

    const delay = parseInt(this.dataset.teaserDelay, 10) || 5000;
    this.teaserTimeout = window.setTimeout(() => {
      if (this.isOpen()) return;
      this.teaserEl.hidden = false;
    }, delay);
  }

  dismissTeaser() {
    if (this.teaserTimeout) window.clearTimeout(this.teaserTimeout);
    if (this.teaserEl) this.teaserEl.hidden = true;
    sessionStorage.setItem('faqChatTeaserDismissed', 'true');
  }

  handleClick = (event) => {
    if (event.target.closest('[data-faq-chat-teaser-open]')) {
      this.dismissTeaser();
      this.openDialog();
      return;
    }

    if (event.target.closest('[data-faq-chat-teaser-close]')) {
      this.dismissTeaser();
      return;
    }

    if (event.target.closest('[data-faq-chat-open]')) {
      this.dismissTeaser();
      this.toggleDialog();
      return;
    }

    if (event.target.closest('[data-faq-chat-close]')) {
      this.closeDialog();
      return;
    }

    const topicButton = event.target.closest('[data-faq-chat-topic]');
    if (topicButton) {
      this.openConversation(topicButton);
      return;
    }

    if (event.target.closest('[data-faq-chat-back]')) {
      this.showMenu();
    }
  };

  isOpen() {
    return this.panelWrapper?.classList.contains('is-open');
  }

  toggleDialog() {
    if (this.isOpen()) {
      this.closeDialog();
    } else {
      this.openDialog();
    }
  }

  openDialog() {
    this.panelWrapper?.classList.add('is-open');
    this.triggerButton.setAttribute('aria-expanded', 'true');
    if (this.searchInput) {
      window.requestAnimationFrame(() => this.searchInput.focus());
    }
  }

  closeDialog() {
    this.panelWrapper?.classList.remove('is-open');
    this.triggerButton.setAttribute('aria-expanded', 'false');
  }

  filterTopics(query) {
    const normalized = query.trim().toLowerCase();
    const categories = this.topicsEl.querySelectorAll('[data-faq-chat-category]');
    let anyVisible = false;

    categories.forEach((category) => {
      const topics = category.querySelectorAll('[data-faq-chat-topic]');
      let categoryHasVisible = false;

      topics.forEach((topic) => {
        const matches = !normalized || topic.dataset.topicSearch.includes(normalized);
        topic.hidden = !matches;
        if (matches) categoryHasVisible = true;
      });

      category.hidden = !categoryHasVisible;
      if (categoryHasVisible) anyVisible = true;
    });

    if (this.emptyStateEl) this.emptyStateEl.hidden = anyVisible;
  }

  openConversation(topicButton) {
    const question = topicButton.dataset.topicQuestion;
    const answerTemplate = topicButton.querySelector('[data-topic-answer]');
    const answerHtml = answerTemplate ? answerTemplate.innerHTML : '';

    this.conversationEl.innerHTML = '';

    const userBubble = document.createElement('div');
    userBubble.className = 'faq-chat__bubble faq-chat__bubble--user';
    userBubble.textContent = question;
    this.conversationEl.appendChild(userBubble);

    const typingBubble = document.createElement('div');
    typingBubble.className = 'faq-chat__bubble faq-chat__bubble--bot faq-chat__bubble--typing';
    typingBubble.innerHTML = '<span></span><span></span><span></span>';
    this.conversationEl.appendChild(typingBubble);

    this.menuPanel.hidden = true;
    this.conversationPanel.hidden = false;
    this.body.scrollTop = 0;

    // Pequeno atraso só pra dar a sensação de "digitando" — não é IA, é o
    // mesmo conteúdo estático já cadastrado no block, só com um respiro visual
    // antes de aparecer, pra não parecer que a resposta simplesmente "piscou".
    window.setTimeout(() => {
      typingBubble.remove();
      const botBubble = document.createElement('div');
      botBubble.className = 'faq-chat__bubble faq-chat__bubble--bot';
      botBubble.innerHTML = answerHtml;
      this.conversationEl.appendChild(botBubble);
    }, 400);
  }

  showMenu() {
    this.conversationPanel.hidden = true;
    this.menuPanel.hidden = false;
    this.conversationEl.innerHTML = '';
  }
}

if (!customElements.get('faq-chat-widget')) {
  customElements.define('faq-chat-widget', FaqChatWidget);
}
